import { useState, useRef, useCallback } from 'react'
import './ProfileFilterBar.css'

function FilterDropdown({ filterKey, label, options, selected, onChange, showSearch }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)

  const handleBackdrop = useCallback(() => { setOpen(false); setSearch('') }, [])
  const filtered = search ? options.filter(o => (o.label||o.value).toLowerCase().includes(search.toLowerCase())) : options
  const isSelected = (val) => selected.includes(val)
  const toggle = (val) => onChange(filterKey, isSelected(val) ? selected.filter(v=>v!==val) : [...selected, val])
  const selectAll = () => onChange(filterKey, options.map(o=>o.value))
  const selectNone = () => onChange(filterKey, [])
  const hasSelection = selected.length > 0 && selected.length < options.length

  let triggerText = label
  if (selected.length === options.length) triggerText = `${label}: All`
  else if (selected.length === 1) { const m = options.find(o=>o.value===selected[0]); triggerText = `${label}: ${m?.label||selected[0]}` }

  return (
    <div className="pfb-drawer-filter" ref={ref}>
      {open && <div className="pfb-backdrop" onClick={handleBackdrop} />}
      <button
        className={`pfb-drawer-trigger ${open?'active':''} ${hasSelection?'has-selection':''}`}
        onClick={() => { setOpen(!open); setSearch('') }}
      >
        <span className="pfb-drawer-trigger-label">{triggerText}</span>
        {selected.length > 1 && selected.length < options.length && <span className="pfb-trigger-count">{selected.length}</span>}
        <span className="pfb-trigger-chevron">▾</span>
      </button>
      {open && (
        <div className="pfb-dropdown pfb-dropdown-drawer">
          <div className="pfb-dropdown-header">
            <span className="pfb-dropdown-title">{label}</span>
            <div className="pfb-dropdown-actions">
              <button className="pfb-dropdown-action" onClick={selectAll}>All</button>
              <button className="pfb-dropdown-action" onClick={selectNone}>None</button>
            </div>
          </div>
          {showSearch && options.length > 6 && (
            <div className="pfb-dropdown-search">
              <input type="text" placeholder={`Search ${label.toLowerCase()}…`} value={search} onChange={e=>setSearch(e.target.value)} autoFocus />
            </div>
          )}
          <div className="pfb-options-list">
            {filtered.map(opt => (
              <div key={opt.value} className={`pfb-option ${isSelected(opt.value)?'selected':''}`} onClick={()=>toggle(opt.value)}>
                <div className="pfb-option-check">{isSelected(opt.value)?'✓':''}</div>
                <span className="pfb-option-label" title={opt.label||opt.value}>{opt.label||opt.value}</span>
                {opt.count != null && <span className="pfb-option-count">{opt.count.toLocaleString()}</span>}
              </div>
            ))}
            {filtered.length === 0 && <div style={{padding:'12px',fontSize:'12px',color:'var(--gray-400)',textAlign:'center'}}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  )
}

function SingleSelectDropdown({ filterKey, label, options, selected, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const currentVal = selected?.[0] || null
  const currentOpt = options.find(o => o.value === currentVal)
  const triggerText = currentOpt ? `${label}: ${currentOpt.label}` : label

  return (
    <div className="pfb-drawer-filter" ref={ref}>
      {open && <div className="pfb-backdrop" onClick={() => setOpen(false)} />}
      <button
        className={`pfb-drawer-trigger ${open ? 'active' : ''} ${currentVal ? 'has-selection' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span className="pfb-drawer-trigger-label">{triggerText}</span>
        <span className="pfb-trigger-chevron">▾</span>
      </button>
      {open && (
        <div className="pfb-dropdown pfb-dropdown-drawer">
          <div className="pfb-dropdown-header">
            <span className="pfb-dropdown-title">{label}</span>
          </div>
          <div className="pfb-options-list">
            {options.map(opt => (
              <div
                key={opt.value}
                className={`pfb-option ${currentVal === opt.value ? 'selected' : ''}`}
                onClick={() => { onChange(filterKey, [opt.value]); setOpen(false) }}
              >
                <div className="pfb-option-check">{currentVal === opt.value ? '●' : ''}</div>
                <span className="pfb-option-label" title={opt.label}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function ProfileFilterBar({ customerId, filterData, activeFilters, onFiltersChange, inlineMode = false, activeTab = null, activeMetricModule = null }) {
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (!filterData?.filters) return null
  const filters = filterData.filters

  // Filter out tab-scoped options (e.g. pay_date only shows on payroll/benefits/time_attendance)
  const PAY_METRIC_MODULES = new Set(['payroll', 'benefits', 'time_attendance'])
  const visibleFilters = Object.fromEntries(
    Object.entries(filters).filter(([key, def]) => {
      if (!def.tabs) return true  // no tab restriction — always show
      // Tab-scoped filters: show whenever on the workforce (Metrics) tab
      // pay_date is useful across all metric modules when on that tab
      if (activeTab !== 'workforce') return false
      return true
    })
  )

  const filterKeys = Object.keys(visibleFilters)
  if (filterKeys.length === 0) return null

  const hasActiveFilters = filterKeys.some(key => {
    const sel = activeFilters[key] || []
    const opts = filters[key]?.options || []
    const defaults = filters[key]?.default || []
    if (defaults.length > 0) {
      const ds = new Set(defaults); const ss = new Set(sel)
      if (ds.size !== ss.size) return true
      for (const d of ds) { if (!ss.has(d)) return true }
      return false
    }
    return sel.length > 0 && sel.length < opts.length
  })

  const activeCount = filterKeys.filter(key => {
    const sel = activeFilters[key] || []
    const opts = filters[key]?.options || []
    const defaults = filters[key]?.default || []
    if (defaults.length > 0) {
      const ds = new Set(defaults); const ss = new Set(sel)
      if (ds.size !== ss.size) return true
      for (const d of ds) { if (!ss.has(d)) return true }
      return false
    }
    return sel.length > 0 && sel.length < opts.length
  }).length

  const handleChange = (key, values) => onFiltersChange({ ...activeFilters, [key]: values })

  const handleReset = () => {
    const defaults = {}
    for (const key of filterKeys) {
      const fd = filters[key]
      if (fd.default) {
        const available = new Set(fd.options.map(o=>o.value))
        defaults[key] = fd.default.filter(d=>available.has(d))
      } else {
        defaults[key] = fd.options.map(o=>o.value)
      }
    }
    onFiltersChange(defaults)
  }

  const orderedKeys = []
  if (visibleFilters.employee_status) orderedKeys.push('employee_status')
  if (visibleFilters.company) orderedKeys.push('company')
  if (visibleFilters.country) orderedKeys.push('country')
  for (let i=1; i<=4; i++) { const k=`org_level_${i}`; if (visibleFilters[k]) orderedKeys.push(k) }
  for (const key of filterKeys) { if (!orderedKeys.includes(key)) orderedKeys.push(key) }
  // Pay date always last
  if (visibleFilters.pay_date && !orderedKeys.includes('pay_date')) orderedKeys.push('pay_date')

  const getLabel = (key) => {
    if (key === 'employee_status') return 'Status'
    if (key === 'company') return 'Company'
    if (key === 'country') return 'Country'
    if (key === 'pay_date') return 'Pay Date'
    if (visibleFilters[key]?.label) return visibleFilters[key].label
    return key.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase())
  }

  // ── Section grouping ──────────────────────────────────────────────────────
  // The drawer used to render one flat list of every axis across every domain.
  // Group by the domain tag the backend attaches to each axis (hcm / finance /
  // wfm), and split WFM into its structural skeleton (labor levels + job) vs its
  // fact axes, so the drawer reads as sections instead of one giant filter.
  const domainOf = (key) => visibleFilters[key]?.domain || 'hcm'
  const isWfmStructural = (key) => key.startsWith('wfm_ll') || key === 'wfm_job'
  const SECTIONS = [
    { id: 'hcm',     label: 'HCM',            test: (k) => domainOf(k) === 'hcm' },
    { id: 'finance', label: 'Finance',        test: (k) => domainOf(k) === 'finance' },
    { id: 'wfm_s',   label: 'WFM · Structure', test: (k) => domainOf(k) === 'wfm' && isWfmStructural(k) },
    { id: 'wfm_f',   label: 'WFM · Facts',     test: (k) => domainOf(k) === 'wfm' && !isWfmStructural(k) },
  ]
  const buildGroups = () => {
    const groups = SECTIONS.map(s => ({ ...s, keys: [] }))
    for (const key of orderedKeys) {
      const g = groups.find(s => s.test(key))
      if (g) g.keys.push(key)
      else groups[0].keys.push(key)  // fallback: any untagged axis lands under HCM
    }
    return groups.filter(g => g.keys.length > 0)
  }

  const renderPill = (key) => (
    visibleFilters[key]?.single_select
      ? <SingleSelectDropdown key={key} filterKey={key} label={getLabel(key)} options={visibleFilters[key].options} selected={activeFilters[key] || []} onChange={handleChange} />
      : <FilterDropdown key={key} filterKey={key} label={getLabel(key)} options={visibleFilters[key].options} selected={activeFilters[key] || []} onChange={handleChange} showSearch={key !== 'employee_status' && key !== 'country'} />
  )
  // If every visible axis is one domain, headers add noise — render flat.
  const groups = buildGroups()
  const showHeaders = groups.length > 1
  const renderSections = () => groups.map(g => (
    <div className="pfb-section" key={g.id}>
      {showHeaders && <div className="pfb-section-head">{g.label}</div>}
      {g.keys.map(renderPill)}
    </div>
  ))
  // Filters the server could not load are named, never just left out.
  const unavailableNotes = Object.entries(filterData.unavailable || {}).map(([domain, msg]) => (
    <div className="pfb-unavailable" key={`unavailable-${domain}`} role="status">{msg}</div>
  ))

  // Inline mode: render filter dropdowns directly (used inside cpg-filter-drawer)
  if (inlineMode) {
    return (
      <div className="pfb-inline">
        {hasActiveFilters && (
          <div className="pfb-inline-reset-row">
            <button className="pfb-reset" onClick={handleReset}>Reset all filters</button>
          </div>
        )}
        {unavailableNotes}
        {renderSections()}
      </div>
    )
  }

  return (
    <>
      {/* Trigger button in the filter bar area */}
      <div className="pfb-bar pfb-bar-compact">
        <button className={`pfb-filters-btn ${drawerOpen?'active':''}`} onClick={()=>setDrawerOpen(true)}>
          <span className="pfb-filters-icon">⏣</span>
          Filters
          {activeCount > 0 && <span className="pfb-filters-active-count">{activeCount}</span>}
          <span className="pfb-filters-chevron">›</span>
        </button>
        {hasActiveFilters && (
          <>
            <div className="pfb-divider" />
            <button className="pfb-reset" onClick={handleReset}>Reset Filters</button>
          </>
        )}
      </div>

      {/* Drawer overlay */}
      {drawerOpen && <div className="pfb-drawer-backdrop" onClick={()=>setDrawerOpen(false)} />}

      {/* Drawer panel */}
      <div className={`pfb-drawer ${drawerOpen?'open':''}`}>
        <div className="pfb-drawer-head">
          <span className="pfb-drawer-title">Filters</span>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {hasActiveFilters && <button className="pfb-reset" onClick={handleReset}>Reset</button>}
            <button className="pfb-drawer-close" onClick={()=>setDrawerOpen(false)}>✕</button>
          </div>
        </div>
        <div className="pfb-drawer-body">
          {unavailableNotes}
          {renderSections()}
        </div>
      </div>
    </>
  )
}


